import {
  FONT_STACK,
  financeControlSkin,
  sureco,
} from '../shared/finance.styles'
import styled from 'styled-components'

export const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  color: ${sureco.fg1};
  font-family: ${FONT_STACK};

  ${financeControlSkin}
`

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

/**
 * Sized to match the Finance managers tab, down to the 34px control height, so
 * the filter row does not shift when you switch tabs.
 */
export const SearchInputWrap = styled.div`
  flex: 0 1 320px;
  /* Holds the placeholder without clipping it. */
  min-width: 260px;

  .ant-input-affix-wrapper {
    width: 100%;
    height: 34px;
    padding: 0 12px;
  }
`

export const PaymentTypeFilterWrap = styled.div`
  flex: 0 0 auto;
  width: 180px;

  .ant-select {
    width: 100%;
  }
`

export const StatusFilterWrap = styled.div`
  flex: 0 0 auto;
  width: 200px;

  .ant-select {
    width: 100%;
  }
  .ant-select-selector {
    height: 34px !important;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: 32px !important;
    font-size: 14px;
  }
`

export const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`

export const TableCard = styled.div`
  background: ${sureco.white};
  border-radius: 12px;
  border: 1px solid ${sureco.border};
  box-shadow: ${sureco.shadowSm};
  /* The table owns its own horizontal scroll; an outer one would drag the
     frozen company column out of view with it. */
  overflow: hidden;

  /* Tripled to outrank antd's own \`.ant-table-small\` density rules. */
  &&& .ant-table-thead > tr > th {
    background: ${sureco.offWhite};
    color: ${sureco.fg1};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    height: 48px;
    border-bottom: 1px solid ${sureco.border};
    padding: 0 8px 0 12px;
  }

  /* Keep antd's column separator but draw it as the design's short divider. */
  .ant-table-thead > tr > th:not(:last-child)::before {
    background-color: ${sureco.borderStrong} !important;
    width: 2px !important;
    height: 14px !important;
  }

  &&& .ant-table-tbody > tr > td {
    font-size: 14px;
    line-height: 18px;
    color: ${sureco.fg1};
    vertical-align: middle;
    border-bottom: 1px solid ${sureco.border};
    height: 42px;
    padding: 0 12px;
  }

  .ant-table-tbody > tr:hover > td {
    background: ${sureco.offWhite};
  }

  /* The whole row opens the profile, so it has to read as a target. */
  .ant-table-tbody > tr.row-clickable {
    cursor: pointer;
  }

  /* A deactivated company sends no invoices, so it recedes. Muted text rather
     than opacity: the frozen Company name column would turn translucent and
     show the scrolling columns through itself. */
  .ant-table-tbody > tr.row-inactive > td {
    color: ${sureco.fg3};
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  /* Pager on the left, page size on the right, as in the design. */
  .ant-table-pagination.ant-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 14px 20px;
    margin: 0 !important;
    border-top: 1px solid ${sureco.border};
    background: ${sureco.white};
    border-radius: 0 0 12px 12px;
    gap: 6px;
  }

  /* Outranks antd's \`.ant-pagination-mini\` rules on these elements. */
  &&& .ant-pagination-options {
    margin-inline-start: auto;
  }
  &&& .ant-pagination-options .ant-select {
    width: 240px;
    height: 36px;
  }
  &&& .ant-pagination-options .ant-select-selector {
    height: 36px;
    border-radius: 6px;
    border-color: ${sureco.borderStrong};
  }
  &&& .ant-pagination-options .ant-select-selection-item {
    line-height: 34px;
    font-size: 14px;
    color: ${sureco.fg1};
  }

  &&& .ant-pagination-item {
    border-radius: 6px;
    border: 1px solid ${sureco.border};
    background: ${sureco.white};
    min-width: 32px;
    height: 32px;
    line-height: 30px;
    margin: 0;
    transition: all 0.15s;
  }

  .ant-pagination-item a {
    font-size: 14px;
    font-weight: 500;
    color: ${sureco.fg1};
  }

  &&& .ant-pagination-item-active {
    border-color: ${sureco.teal};
    background: ${sureco.teal};
  }

  &&& .ant-pagination-item-active a {
    color: ${sureco.onTeal};
    font-weight: 600;
  }

  .ant-pagination-item:not(.ant-pagination-item-active):hover {
    border-color: ${sureco.teal} !important;
  }

  .ant-pagination-item:not(.ant-pagination-item-active):hover a {
    color: ${sureco.teal} !important;
  }

  &&& .ant-pagination-prev,
  &&& .ant-pagination-next {
    min-width: 32px;
    height: 32px;
    line-height: 30px;
    border-radius: 6px;
    margin: 0;
  }

  &&& .ant-pagination-prev .ant-pagination-item-link,
  &&& .ant-pagination-next .ant-pagination-item-link {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: ${sureco.white};
    color: ${sureco.fg2};
    border: 1px solid ${sureco.border};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
  }

  .ant-pagination-prev:hover .ant-pagination-item-link,
  .ant-pagination-next:hover .ant-pagination-item-link {
    border-color: ${sureco.teal} !important;
    color: ${sureco.teal} !important;
  }

  .ant-pagination-disabled .ant-pagination-item-link {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Mixed with white rather than transparent, so the frozen Company name cell
     stays opaque while the flash plays. */
  /* Mixed with white rather than transparent: the frozen Company name cell has
     to stay opaque while the flash plays. */
  @keyframes rowHighlight {
    0% {
      background: color-mix(in srgb, ${sureco.teal} 18%, ${sureco.white});
    }
    60% {
      background: color-mix(in srgb, ${sureco.teal} 18%, ${sureco.white});
    }
    100% {
      background: ${sureco.white};
    }
  }

  .ant-table-tbody > tr.row-just-updated > td {
    animation: rowHighlight 1.8s ease-out forwards;
  }
`

export const CompanyCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.fg1};
  line-height: 1.4;
`

export const DueDayCell = styled.span`
  color: ${sureco.fg1};
  font-variant-numeric: tabular-nums;
`

export const SyncCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${sureco.fg1};
`

export const SyncSourceTag = styled.span<{ $auto: boolean }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  padding: 1px 7px;
  border-radius: 9999px;
  background: ${({ $auto }) => ($auto ? sureco.offWhite : sureco.white)};
  border: 1px solid
    ${({ $auto }) => ($auto ? sureco.borderStrong : sureco.border)};
  color: ${({ $auto }) => ($auto ? sureco.fg2 : sureco.fg3)};
`

export const KebabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${sureco.border};
  background: ${sureco.white};
  color: ${sureco.fg2};
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s;

  &:hover {
    border-color: ${sureco.teal};
    color: ${sureco.teal};
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${sureco.focusRing};
  }
`

export const ShowingBar = styled.div`
  font-size: 14px;
  color: ${sureco.fg3};
`

export const ModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export const ModalForm = styled.div`
  width: 100%;
`

export const ActivityPanel = styled.div`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
  overflow: hidden;
`

export const ActivityPanelToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  background: none;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f3f4f6;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${sureco.focusRing};
  }
`

export const ActivityPanelToggleIcon = styled.span`
  margin-left: auto;
  font-size: 10px;
  color: #9ca3af;
  line-height: 1;
`

export const RadioOptionRow = styled.div`
  display: flex;
  gap: 20px;

  .ant-radio-wrapper {
    margin: 0;
    font-size: 13px;
    color: ${sureco.fg1};
  }
`

/**
 * Caps the recipient list: a company with a dozen addresses would otherwise
 * stretch the dialog past the viewport.
 */
export const EmailTagsField = styled.div`
  .ant-select-selector {
    max-height: 120px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;
  }
`

export const ActivityLine = styled.div`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  background: #ffffff;
  font-size: 12px;
  color: #374151;
`

/* Scrolls rather than pages: the whole history arrives in one fetch. */
export const ActivityEntriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  max-height: 220px;
  overflow-y: auto;
`

export const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 3px;

  &::before {
    content: '';
    display: inline-block;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #d1d5db;
  }
`

export const ActivityEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 8px;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
  padding: 32px 0;
`

export const FormLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151;
  margin-bottom: 4px;
`

export const FormHint = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`

export const ModalScrollBody = styled.div`
  height: 560px;
  overflow-y: auto;
  padding-right: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 99px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`

export const UploadArea = styled.div`
  border: 2px dashed ${sureco.borderStrong};
  border-radius: 12px;
  padding: 40px 24px;
  text-align: center;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background 0.2s;

  &:hover {
    border-color: ${sureco.teal};
    background: ${sureco.offWhite};
  }
  &:focus-visible {
    outline: 2px solid ${sureco.teal};
    outline-offset: 2px;
  }
`

export const UploadSelectLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const UploadDropTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`

export const UploadDropHint = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`

export const UploadSelectLinks = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;

  .ant-btn-link {
    font-size: 13px;
    color: ${sureco.teal};
  }
`

export const UploadPreviewFileName = styled.span`
  font-weight: 600;
`

export const UploadPreviewFileMeta = styled.span`
  color: #6b7280;
  margin-left: 8px;
`

/* Same colour language as the results step: green creates, teal updates,
   grey does nothing, red cannot be uploaded. */
const PLAN_PILL_COLORS = {
  create: {
    bg: sureco.successLight,
    border: sureco.successBorder,
    fg: sureco.successText,
  },
  update: { bg: sureco.white, border: sureco.teal, fg: sureco.teal },
  unchanged: { bg: sureco.white, border: sureco.border, fg: sureco.fg3 },
  needsEmails: {
    bg: sureco.errorLight,
    border: sureco.errorBorder,
    fg: sureco.errorText,
  },
} as const

export const PlanPill = styled.span<{
  $variant: keyof typeof PLAN_PILL_COLORS
}>`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
  border-radius: 9999px;
  background: ${({ $variant }) => PLAN_PILL_COLORS[$variant].bg};
  border: 1px solid ${({ $variant }) => PLAN_PILL_COLORS[$variant].border};
  color: ${({ $variant }) => PLAN_PILL_COLORS[$variant].fg};
`

export const UploadSuccessNote = styled.div`
  text-align: center;
  padding: 24px;
  color: #16a34a;
  font-size: 14px;

  > div {
    font-weight: 600;
  }
`

export const UploadResultSummary = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`

export const UploadResultStat = styled.div<{ $color?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  span:first-child {
    font-size: 24px;
    font-weight: 700;
    color: ${({ $color }) => $color ?? '#111827'};
  }

  span:last-child {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
  }
`

export const UploadPaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 4px 0;

  > span {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6b7280;
  }

  .upload-pagination .ant-pagination-item,
  .upload-pagination .ant-pagination-prev,
  .upload-pagination .ant-pagination-next,
  .upload-pagination .ant-pagination-jump-prev,
  .upload-pagination .ant-pagination-jump-next {
    margin-inline-end: 6px;
  }

  .upload-pagination .ant-pagination-total-text {
    margin-inline-end: 12px;
  }
`

export const UploadPreviewTable = styled.div`
  .ant-table-tbody > tr > td {
    vertical-align: middle;
  }

  .ant-table-tbody > tr.row-client-unmatched > td,
  .ant-table-tbody > tr.row-invalid > td {
    background: #fef2f2 !important;
    vertical-align: bottom;
  }
`

export const UploadErrorList = styled.div`
  max-height: 220px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 16px;
  row-gap: 4px;
  align-items: baseline;
`

export const UploadErrorItem = styled.div`
  display: contents;
  font-size: 12px;
  line-height: 1.5;

  > span:first-child {
    font-weight: 600;
    color: #991b1b;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  > span:last-child {
    color: #b91c1c;
    word-break: break-word;
  }
`

export const FieldErrorMessage = styled.div`
  color: #dc2626;
  font-size: 11px;
  margin-bottom: 4px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.3;
`

export const InvalidDateInput = styled.input<{ $invalid?: boolean }>`
  width: 100%;
  font-size: 12px;
  padding: 2px 4px;
  border: 1px solid ${({ $invalid }) => ($invalid ? '#dc2626' : '#d9d9d9')};
  border-radius: 2px;
  outline: none;

  &:focus {
    border-color: ${({ $invalid }) => ($invalid ? '#dc2626' : sureco.teal)};
    box-shadow: 0 0 0 2px
      ${({ $invalid }) =>
        $invalid ? 'rgba(220, 38, 38, 0.1)' : 'rgba(1, 160, 168, 0.15)'};
  }
`

export const UploadErrorBanner = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #b91c1c;
  font-size: 13px;
  align-items: flex-start;
`

export const UploadErrorBannerHeader = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 13px;
`

export const UploadErrorGroup = styled.div`
  margin-top: 10px;

  &:first-of-type {
    margin-top: 0;
  }
`

export const UploadErrorGroupHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #7f1d1d;
  margin-bottom: 4px;
  display: flex;
  gap: 6px;
  align-items: baseline;
  flex-wrap: wrap;
`

export const UploadErrorGroupHint = styled.span`
  font-weight: 400;
  color: #b91c1c;
  font-size: 11px;
`

export const UploadErrorRowBadgeList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  font-variant-numeric: tabular-nums;
`

export const UploadErrorRowBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #fee2e2;
  color: #991b1b;
  font-size: 11px;
  font-weight: 600;

  > em {
    font-style: normal;
    font-weight: 400;
    color: #b91c1c;
  }
`

export const ModalBody = styled.div`
  padding: 4px 0 8px;
`

export const ModalDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 16px;
`

export const SpacedFormItemWrapper = styled.div`
  margin-top: 12px;
`

export const StripeAccountList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const StripeAccountCard = styled.div`
  padding: 10px 12px;
  border: 1px solid ${sureco.border};
  border-radius: 8px;
  background: ${sureco.offWhite};

  .ant-form-item {
    margin-bottom: 0;
  }
`

export const StripeAccountHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 13px;
`

/** Description takes the room; the active switch sits at the end of its line. */
export const StripeAccountFields = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  > .ant-form-item:first-child {
    flex: 1;
    min-width: 0;
  }
`

export const StripeActiveToggle = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${sureco.fg2};
`

export const StripeCustomerId = styled.span`
  font-family: monospace;
  color: ${sureco.fg1};
  overflow-wrap: anywhere;
`

export const StripeBankLast4 = styled.span`
  flex-shrink: 0;
  color: ${sureco.fg3};
`

export const StripeAccountEmpty = styled.div`
  font-size: 13px;
  color: ${sureco.fg3};
`

// ─── HubSpot sync: overlay, task banner, and the three decision modals ───────

export const SyncConfirmBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 13px;
  color: ${sureco.fg2};

  p {
    margin: 0;
  }
`

export const SyncConfirmList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${sureco.fg3};
`

export const SyncingOverlayBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 32px 16px 8px;
  text-align: center;
`

export const SyncingOverlayTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${sureco.fg1};
`

export const SyncingOverlayStep = styled.div`
  font-size: 13px;
  color: ${sureco.fg3};
  min-height: 20px;
`

export const TaskBanner = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${sureco.noticeLight};
  border: 1px solid ${sureco.noticeBorder};
`

export const TaskBannerIcon = styled.span`
  color: ${sureco.noticeText};
  font-size: 16px;
  line-height: 22px;
  flex-shrink: 0;
`

export const TaskBannerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

export const TaskBannerHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.noticeText};
`

/** What a recipient removal costs each person, shown in its confirm dialog. */
export const RemovalNotice = styled.div`
  margin-bottom: 18px;
`

/** Keeps a long recipient list from pushing the dialog's buttons off screen. */
export const RemovalScroll = styled.div`
  max-height: min(50vh, 440px);
  overflow-y: auto;
  padding-right: 4px;
`

export const RemovalLoss = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: ${sureco.fg2};
`

export const RemovalChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

export const RemovalChip = styled.span`
  padding: 2px 8px;
  font-size: 12px;
  line-height: 18px;
  color: ${sureco.fg2};
  background: ${sureco.offWhite};
  border: 1px solid ${sureco.border};
  border-radius: 12px;
  word-break: break-all;
`

export const RemovalCompanyRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`

export const TaskList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const TaskRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${sureco.fg2};

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${sureco.noticeText};
    flex-shrink: 0;
  }
`

export const SyncRowActions = styled.div`
  display: flex;
  justify-content: flex-end;
`

export const SyncStepGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid ${sureco.border};
  background: ${sureco.white};
`

export const SyncStepGroupTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.fg1};
`

export const SyncFieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`

export const SyncField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const SyncFieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${sureco.fg2};
`

export const SyncFieldHint = styled.div`
  font-size: 12px;
  color: ${sureco.fg3};
`

/** Why the last submit refused this row, shown in place on the row itself. */
export const SyncSubmitError = styled.div`
  font-size: 12px;
  color: ${sureco.errorText};
`

export const InviteChipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const InviteChipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid ${sureco.borderStrong};
  background: ${sureco.offWhite};
  font-size: 13px;
  color: ${sureco.fg2};

  .ant-checkbox-wrapper {
    flex-shrink: 0;
    font-size: 12px;
    color: ${sureco.fg3};
  }

  .anticon-close {
    flex-shrink: 0;
    color: ${sureco.fg3};
    cursor: pointer;
  }
  .anticon-close:hover {
    color: ${sureco.danger};
  }
`

export const InviteChipEmail = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const SyncModalScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: min(62vh, 560px);
  overflow-y: auto;
  padding-right: 8px;

  /* A shadow on whichever edge has more behind it, so a list cut off at the
     fold does not read as the end of it. The local/scroll pairing is what
     makes each one appear only while there is something to scroll to. */
  background:
    linear-gradient(#fff 30%, rgba(255, 255, 255, 0)) top / 100% 20px no-repeat,
    linear-gradient(rgba(255, 255, 255, 0), #fff 70%) bottom / 100% 20px
      no-repeat,
    radial-gradient(farthest-side at 50% 0, rgba(15, 23, 42, 0.12), transparent)
      top / 100% 7px no-repeat,
    radial-gradient(
        farthest-side at 50% 100%,
        rgba(15, 23, 42, 0.12),
        transparent
      )
      bottom / 100% 7px no-repeat;
  background-attachment: local, local, scroll, scroll;

  scrollbar-width: thin;
  scrollbar-color: ${sureco.borderStrong} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${sureco.borderStrong};
    border-radius: 99px;
  }
`

export const SyncModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid ${sureco.border};
  padding-top: 16px;
  margin-top: 4px;
`

export const SyncFooterSummary = styled.div`
  font-size: 13px;
  color: ${sureco.fg3};
  line-height: 1.45;
  max-width: 520px;
`

export const ModalSubtitle = styled.p`
  font-size: 13px;
  color: ${sureco.fg3};
  margin: 0 0 16px;
`

export const SyncEmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: ${sureco.fg3};
  font-size: 13px;
`

export const ReviewCard = styled.section`
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid ${sureco.border};
  background: ${sureco.white};
  box-shadow: ${sureco.shadowSm};
`

export const ReviewCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
`

export const ReviewCardTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.fg1};
`

/** Email, Company, Reason, then the row's own decision. */
export const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns:
    minmax(160px, 1.2fr) minmax(120px, 1fr) minmax(180px, 1.6fr)
    auto;
  gap: 8px 12px;
  align-items: baseline;
  font-size: 13px;
  color: ${sureco.fg2};
`

export const ReviewGridHeadCell = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${sureco.fg3};
  border-bottom: 1px solid ${sureco.borderSoft};
  padding-bottom: 4px;
`

export const ReviewReasonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const ReviewGridEmail = styled.div`
  overflow-wrap: anywhere;
  color: ${sureco.fg1};
`

export const ReviewBulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  /* A separator between removing everything and walking away, so the two are
     not a pair of adjacent links. */
  .review-bulk-divider {
    width: 1px;
    height: 14px;
    background: ${sureco.border};
  }
`

/** Whole-list shortcuts above the per-company rows in the Changes modal. */
/**
 * The two decisions the run asks for, one master control each: which companies
 * to bring in line, and which of their new addresses also get portal access.
 * Both are tri-state, so the bar doubles as the running tally.
 */
export const SyncBulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border: 1px solid ${sureco.border};
  border-radius: 10px;
  background: ${sureco.offWhite};

  .ant-checkbox-wrapper {
    align-items: center;
  }
`

export const SyncBulkLabel = styled.span`
  font-size: 13px;
  color: ${sureco.fg2};
`

export const SyncBulkCount = styled.span`
  font-weight: 600;
  color: ${sureco.fg1};
  font-variant-numeric: tabular-nums;
`

export const ReviewReason = styled.span<{ $tone: 'warning' | 'error' }>`
  font-size: 13px;
  color: ${({ $tone }) =>
    $tone === 'error' ? sureco.errorText : sureco.noticeText};
`

export const ReviewActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

export const ReviewSectionTitle = styled.div`
  margin: 16px 0 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${sureco.fg3};
`

export const ReviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${sureco.border};
  border-radius: 8px;
  background: ${sureco.white};

  & + & {
    margin-top: 8px;
  }
`

export const ReviewRowMain = styled.div`
  flex: 1;
  min-width: 0;
`

export const ReviewRowTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.fg1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
